import clsx from 'clsx'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { match } from 'ts-pattern'

import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import OrderDetails from '@/components-latest/ui/OrdersPage/OrderDetails'
import { getScrollOrderId } from '@/utils/order-navigation.utils'
import { useLazyFetchOrdersListQuery } from '@/ducks/orders/services/orders.api'
import { resetOrdersStateToInitial } from '@/ducks/orders/slices/orders.slice'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'

import Styles from './InProgressOrders.module.scss'

const InProgressOrders = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { lmd_store_web_restaurant_chains_feature } = useFlags()

  const storeId = useSelector(selectStoreId)

  const [fetchOrders, { data: _orders, isFetching: isOrdersLoading }] =
    useLazyFetchOrdersListQuery()

  const orders = _orders?.orders
  const shouldBeFaded = orders && orders.length > 0 && isOrdersLoading

  const getOrders = useCallback(() => {
    if (!storeId) return
    fetchOrders({ status: 'ONGOING', store_id: storeId })
  }, [fetchOrders, storeId])

  useEffect(() => {
    getOrders()
  }, [getOrders, storeId])

  useEffect(() => {
    return () => {
      dispatch(resetOrdersStateToInitial())
    }
  }, [dispatch])

  const location = useLocation()
  const queryOrderId = getScrollOrderId(location.search)

  return (
    <div className={clsx(Styles.orders, { [Styles.faded]: shouldBeFaded })}>
      {match({ orders, isOrdersLoading })
        .with(
          { isOrdersLoading: true },
          ({ orders }) => !orders || orders.length === 0,
          () => (
            <div className={Styles.message}>
              <Spinner />
            </div>
          )
        )
        .when(
          ({ orders }) => orders && orders.length === 0,
          () => (
            <div className={Styles.message}>
              <img
                className={Styles.imageForEmpty}
                src="/images/emptyOrders.svg"
                alt="Waiting for in progress orders"
              />
              <Text variant="title-3" gutter={8}>
                {t('orders.inprogress-empty-title')}
              </Text>
              <Text variant="subtitle" className={Styles.dimmed}>
                {t('orders.empty-inprogress-message')}
              </Text>
            </div>
          )
        )
        .when(
          ({ orders }) => orders && orders.length > 0,
          ({ orders }) =>
            orders!.map(order => (
              <OrderDetails
                key={order.id}
                order={order}
                timeLeft={order.timeLeft}
                totalTime={order.estimatedPreparationTime}
                actions={['return-to-stock', 'ready']}
                onRefresh={getOrders}
                showBranch={lmd_store_web_restaurant_chains_feature}
                shouldScrollTo={queryOrderId === order.id}
              />
            ))
        )
        .otherwise(() => null)}
    </div>
  )
}

export default InProgressOrders
